/**
 * Authentication Actions Adapter
 *
 * Provides a compatibility layer for loginSlice functions, allowing existing components
 * to continue using the same API while working with both legacy Redux flow and
 * new React Query + Context flow.
 *
 * This adapter pattern solves the loginSlice direct usage problem affecting 14+ files.
 */

import type { CaptchaService } from "main/src/modules/LoginPage/types";
import {
  userLogout as reduxUserLogout,
  getLoginFailure,
  loginUser as reduxLoginUser,
  submitMfaPin as reduxSubmitMfaPin,
  submitOtpLogin as reduxSubmitOtpLogin
} from "main/src/modules/LoginPage/loginSlice";
import { useAppDispatch } from "@certa/common/hooks";
import { useMemo } from "react";
import { useAuth } from "../providers/AuthenticationProvider";
import { isNewAuthFlow } from "../utils/directAuthActions";

export const useAuthActions = () => {
  const authContext = useAuth();
  const authContextClearError = authContext?.clearError;
  const authContextLogin = authContext?.login;
  const authContextLoginMFA = authContext?.loginMFA;
  const authContextLoginOTP = authContext?.loginOTP;
  const authContextLogout = authContext?.logout;

  const dispatch = useAppDispatch();
  const isNewAuthFlowEnabled = isNewAuthFlow();

  const authActions = useMemo(
    () => ({
      userLogout: async () => {
        if (isNewAuthFlowEnabled) {
          return await authContextLogout();
        } else {
          return dispatch(reduxUserLogout());
        }
      },

      clearAuthError: () => {
        if (isNewAuthFlowEnabled) {
          return authContextClearError();
        } else {
          return dispatch(getLoginFailure(null));
        }
      },

      loginUser: async (
        username: string,
        password: string,
        captchaToken: string | null = null,
        captchaService: CaptchaService = "recaptcha"
      ) => {
        if (isNewAuthFlowEnabled) {
          return await authContextLogin({
            username,
            password,
            captchaToken,
            captchaService
          });
        } else {
          const thunk = reduxLoginUser(
            username,
            password,
            captchaToken,
            captchaService
          );
          return await (thunk as any)(dispatch, () => ({}), null);
        }
      },

      submitMfaPin: async (pin: string) => {
        if (isNewAuthFlowEnabled) {
          return await authContextLoginMFA(pin);
        } else {
          const thunk = reduxSubmitMfaPin(pin);
          return await (thunk as any)(dispatch, () => ({}), null);
        }
      },

      submitOtpLogin: async (
        username: string,
        password: string,
        captchaToken: string | null = null,
        captchaService: CaptchaService = "recaptcha"
      ) => {
        if (isNewAuthFlowEnabled) {
          return await authContextLoginOTP({
            username,
            password,
            captchaToken,
            captchaService
          });
        } else {
          const thunk = reduxSubmitOtpLogin(
            username,
            password,
            captchaToken,
            captchaService
          );
          return await (thunk as any)(dispatch, () => ({}), null);
        }
      }
    }),
    [
      isNewAuthFlowEnabled,
      authContextClearError,
      authContextLogin,
      authContextLoginMFA,
      authContextLoginOTP,
      authContextLogout,
      dispatch
    ]
  );

  return authActions;
};

export type AuthActions = ReturnType<typeof useAuthActions>;
